import moment from 'moment';
import { getDescription as getDocumentGlosa } from '../components/matchRowHelpers';

export const STRING_DATE_PARSE_FORMATS = ['YYYY-MM-DD', 'DD/MM/YYYY'];

export const parseStringDateToMoment = (dateString) => {
  if (!dateString || !String(dateString).trim()) {
    return moment().startOf('day');
  }
  const strict = moment(dateString, STRING_DATE_PARSE_FORMATS, true);
  if (strict.isValid()) return strict;
  const loose = moment(dateString);
  return loose.isValid() ? loose : moment().startOf('day');
};

export const stringDateToDateInputValue = (stringDate) => {
  // Preserve empty while editing so the controlled date input can stay blank
  // while the user clears/retypes (same UX as documentos_tributarios filters).
  if (stringDate == null || stringDate === '') return '';
  const m = moment(stringDate, STRING_DATE_PARSE_FORMATS, true);
  return m.isValid() ? m.format('YYYY-MM-DD') : '';
};

export const createEntryDate = (dateString) => {
  const parsedDate = parseStringDateToMoment(dateString);
  const day = parsedDate.format('DD');
  const month = parsedDate.format('MM');
  const year = parsedDate.format('YYYY');
  const fullDate = new Date(parsedDate.year(), parsedDate.month(), parsedDate.date(), 15, 0, 0);
  return {
    day,
    month,
    year,
    fullDate,
  };
};

const resolveDocumentDateSource = (document) => {
  if (!document) return null;
  return (
    document.documentDate ||
    document.meta?.documentDate ||
    document.meta?.date ||
    null
  );
};

const applyRcvDateAdjustment = (
  document,
  defaultStringDate,
  { isTaxDocument, isFromConciliacion }
) => {
  let stringDate = defaultStringDate;
  let rcvPeriod = null;

  if (!isTaxDocument || isFromConciliacion || !document?.meta?.period) {
    return { stringDate, rcvPeriod };
  }

  const documentDateMoment = moment(resolveDocumentDateSource(document) || defaultStringDate);
  const documentYearMonth = documentDateMoment.format('YYYYMM');
  const metaPeriod = String(document.meta.period);

  if (documentYearMonth !== metaPeriod) {
    if (metaPeriod.length === 6) {
      const year = metaPeriod.substring(0, 4);
      const month = metaPeriod.substring(4, 6);
      stringDate = moment(`${year}-${month}-01`, 'YYYY-MM-DD').format('YYYY-MM-DD');
    } else {
      stringDate = moment(documentDateMoment).startOf('month').format('YYYY-MM-DD');
    }
    rcvPeriod = metaPeriod;
  }

  return { stringDate, rcvPeriod };
};

/**
 * Build glosa for an emission entry from a document (TAX_DOCUMENT / OBLIGATION).
 */
export const buildEmissionEntryGlosa = (document) => {
  if (!document) return '';

  if (document.documentType === 'OBLIGATION') {
    const glosaFromDocument = getDocumentGlosa(document);
    if (glosaFromDocument && String(glosaFromDocument).trim()) {
      return glosaFromDocument.trim();
    }
    const fallback = (
      document.description ||
      (typeof document.meta?.description === 'string' ? document.meta.description : '') ||
      (Array.isArray(document.meta?.description) && document.meta.description.length > 0
        ? (typeof document.meta.description[0] === 'string'
          ? document.meta.description[0]
          : (document.meta.description[0]?.description || document.meta.description[0]?.name || ''))
        : '') ||
      ''
    ).trim();
    return fallback;
  }

  const meta = document.meta || {};
  const summaryOrTypeLabel = meta.summary || meta.typeLabel || '';
  const documentIdOrNumber = meta.documentId || meta.number;
  const isReceived = meta.isReceived === true;
  const emitidaPorA = isReceived ? 'emitida por' : 'emitida a';
  const descriptionParts = [];
  if (summaryOrTypeLabel) descriptionParts.push(summaryOrTypeLabel);
  if (documentIdOrNumber) descriptionParts.push(`#${documentIdOrNumber}`);
  descriptionParts.push(emitidaPorA);
  if (isReceived) {
    if (meta.issuerName) descriptionParts.push(meta.issuerName);
    if (meta.issuerId) descriptionParts.push(meta.issuerId);
  } else {
    if (meta.receiverName) descriptionParts.push(meta.receiverName);
    if (meta.receiverId) descriptionParts.push(meta.receiverId);
  }
  let descriptionText = descriptionParts.filter((part) => part).join(' ');
  const lineGlosa = getDocumentGlosa(document);
  if (lineGlosa && String(lineGlosa).trim()) {
    descriptionText = `${descriptionText} :: ${String(lineGlosa).trim()}`;
  }

  return descriptionText;
};

/**
 * Resolve which document to use for glosa in emission/match flows.
 */
export const resolveDocumentForDescription = ({
  entryType,
  document,
  supportDocuments,
  originType,
}) => {
  if (entryType === 'emissionEntry' && document) {
    return document;
  }
  if (entryType === 'matchEntry' && supportDocuments?.length > 0) {
    return supportDocuments.length > 1
      ? (supportDocuments.find((doc) => doc.documentType === originType) ||
          supportDocuments[supportDocuments.length - 1])
      : supportDocuments[0];
  }
  return null;
};

/**
 * Build glosa for modal init (emission + match flows).
 */
export const buildEmissionEntryDescription = ({
  entryType,
  document,
  documentForDescription,
  transaction,
  fallbackDescription = '',
}) => {
  let descriptionText = fallbackDescription || '';

  const obligationDoc =
    documentForDescription?.documentType === 'OBLIGATION' ? documentForDescription : null;

  if (obligationDoc) {
    const glosaFromDocument = getDocumentGlosa(obligationDoc);
    if (glosaFromDocument && String(glosaFromDocument).trim()) {
      return glosaFromDocument.trim();
    }
    const fallback = (
      obligationDoc.description ||
      (typeof obligationDoc.meta?.description === 'string' ? obligationDoc.meta.description : '') ||
      (Array.isArray(obligationDoc.meta?.description) && obligationDoc.meta.description.length > 0
        ? (typeof obligationDoc.meta.description[0] === 'string'
          ? obligationDoc.meta.description[0]
          : (obligationDoc.meta.description[0]?.description ||
            obligationDoc.meta.description[0]?.name ||
            ''))
        : '') ||
      fallbackDescription ||
      ''
    ).trim();
    if (fallback) return fallback;
  } else if (entryType === 'matchEntry' && transaction && !documentForDescription) {
    const txDescription = transaction.description || transaction.name || '';
    if (txDescription.trim()) return txDescription.trim();
  }

  if (documentForDescription && (entryType === 'emissionEntry' || !descriptionText)) {
    if (entryType === 'emissionEntry') {
      const emissionGlosa = buildEmissionEntryGlosa(documentForDescription);
      if (emissionGlosa) return emissionGlosa;
    }

    const isObligation = documentForDescription.documentType === 'OBLIGATION';
    const glosaFromDocument = isObligation ? getDocumentGlosa(documentForDescription) : '';
    if (isObligation && glosaFromDocument && String(glosaFromDocument).trim()) {
      return glosaFromDocument.trim();
    }
    if (isObligation) {
      let obligationGlosa = (
        documentForDescription.description ||
        (typeof documentForDescription.meta?.description === 'string'
          ? documentForDescription.meta.description
          : '') ||
        fallbackDescription ||
        ''
      ).trim();
      if (
        !obligationGlosa &&
        Array.isArray(documentForDescription.meta?.description) &&
        documentForDescription.meta.description.length > 0
      ) {
        const first = documentForDescription.meta.description[0];
        obligationGlosa = (
          typeof first === 'string' ? first : first?.description || first?.name || ''
        ).trim();
      }
      if (obligationGlosa) return obligationGlosa;
    }

    if (!descriptionText) {
      const meta = documentForDescription.meta || {};
      let summaryOrTypeLabel = '';
      if (entryType === 'matchEntry') {
        if (documentForDescription.documentType === 'TAX_DOCUMENT') {
          summaryOrTypeLabel = meta.summary || '';
        } else if (documentForDescription.documentType === 'OBLIGATION') {
          summaryOrTypeLabel = meta.typeLabel || '';
        } else if (documentForDescription.documentType === 'CREDIT') {
          summaryOrTypeLabel =
            documentForDescription.name ||
            (meta.paymentNumber && meta.totalPayments
              ? `Cuota #${meta.paymentNumber} de ${meta.totalPayments}`
              : '');
        } else {
          summaryOrTypeLabel = meta.summary || meta.typeLabel || '';
        }
      } else {
        summaryOrTypeLabel = meta.summary || meta.typeLabel || '';
      }
      const documentIdOrNumber = meta.documentId || meta.number;
      const isReceived = meta.isReceived === true;
      const emitidaPorA = isReceived ? 'emitida por' : 'emitida a';
      const descriptionParts = [];
      if (summaryOrTypeLabel) descriptionParts.push(summaryOrTypeLabel);
      if (documentIdOrNumber) descriptionParts.push(`#${documentIdOrNumber}`);
      descriptionParts.push(emitidaPorA);
      if (isReceived) {
        if (meta.issuerName) descriptionParts.push(meta.issuerName);
        if (meta.issuerId) descriptionParts.push(meta.issuerId);
      } else {
        if (meta.receiverName) descriptionParts.push(meta.receiverName);
        if (meta.receiverId) descriptionParts.push(meta.receiverId);
      }
      descriptionText = descriptionParts.filter((part) => part).join(' ');
      const lineGlosa = getDocumentGlosa(documentForDescription);
      if (lineGlosa && String(lineGlosa).trim()) {
        descriptionText = `${descriptionText} :: ${String(lineGlosa).trim()}`;
      }
    }
  }

  return descriptionText;
};

/**
 * Build stringDate for bulk emission entries.
 */
export const buildEmissionEntryStringDate = (
  document,
  { applyRcvLogic = true, isFromConciliacion = false } = {}
) => {
  const rawDate = resolveDocumentDateSource(document);
  if (!rawDate) {
    return { stringDate: moment().format('YYYY-MM-DD'), rcvPeriod: null };
  }

  const documentDateMoment = moment(rawDate);
  let stringDate = documentDateMoment.format('YYYY-MM-DD');

  if (!applyRcvLogic) {
    return { stringDate, rcvPeriod: null };
  }

  const isTaxDocument = document?.documentType === 'TAX_DOCUMENT';
  return applyRcvDateAdjustment(document, stringDate, { isTaxDocument, isFromConciliacion });
};

/**
 * Build stringDate for modal init (transaction, match, document + RCV).
 */
export const buildEmissionEntryDateFromContext = ({
  entryType,
  document,
  transaction,
  documentSource,
  supportDocuments,
  originType,
  isFromConciliacion = false,
}) => {
  let stringDate = moment().format('YYYY-MM-DD');
  const isTaxDocument =
    document?.documentType === 'TAX_DOCUMENT' || originType === 'TAX_DOCUMENT';

  if (transaction?.date) {
    stringDate = moment(transaction.date).format('YYYY-MM-DD');
    return { stringDate, rcvPeriod: null };
  }

  if (transaction?.stringDate) {
    const parsedDate = moment(transaction.stringDate, STRING_DATE_PARSE_FORMATS, true);
    if (parsedDate.isValid()) {
      return { stringDate: parsedDate.format('YYYY-MM-DD'), rcvPeriod: null };
    }
  }

  if (entryType === 'matchEntry' && documentSource && supportDocuments?.length >= 2) {
    const match = supportDocuments[1];
    const matchDate =
      match?.date || match?.documentDate || match?.stringDate || match?.paymentDate;
    if (matchDate) {
      const matchDateMoment = moment(matchDate);
      if (matchDateMoment.isValid()) {
        return { stringDate: matchDateMoment.format('YYYY-MM-DD'), rcvPeriod: null };
      }
    }
  }

  const dateDocument = document || supportDocuments?.[0];
  const rawDate = resolveDocumentDateSource(dateDocument);
  if (rawDate) {
    const documentDateMoment = moment(rawDate);
    stringDate = documentDateMoment.format('YYYY-MM-DD');
    return applyRcvDateAdjustment(dateDocument, stringDate, {
      isTaxDocument,
      isFromConciliacion,
    });
  }

  return { stringDate, rcvPeriod: null };
};

export const buildBulkDocumentLabel = (document) => {
  if (!document) return '';
  const meta = document.meta || {};
  const parts = [];

  if (document.documentType === 'TAX_DOCUMENT') {
    if (meta.documentId) parts.push(`#${meta.documentId}`);
    const counterparty = meta.isReceived
      ? meta.issuerName || meta.issuerId
      : meta.receiverName || meta.receiverId;
    if (counterparty) parts.push(String(counterparty));
  } else if (document.documentType === 'OBLIGATION') {
    if (meta.typeLabel || meta.type) parts.push(meta.typeLabel || meta.type);
    if (meta.number || meta.documentId) parts.push(`#${meta.number || meta.documentId}`);
  } else if (meta.documentId || meta.number) {
    parts.push(`#${meta.documentId || meta.number}`);
  }

  return parts.filter(Boolean).join(' · ') || document.name || document._id || document.id || '';
};

export const buildBulkDocumentDraft = (
  document,
  { applyRcvLogic = true, isFromConciliacion = false } = {}
) => {
  const documentId = String(document._id || document.id);
  const description = buildEmissionEntryGlosa(document);
  const { stringDate, rcvPeriod } = buildEmissionEntryStringDate(document, {
    applyRcvLogic,
    isFromConciliacion,
  });

  return {
    documentId,
    label: buildBulkDocumentLabel(document),
    description,
    stringDate,
    entryDate: createEntryDate(stringDate),
    rcvPeriod,
  };
};
